import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Plus } from "lucide-react";

export default function AdminUsers() {
  const qc = useQueryClient();
  const { data = [] } = useQuery({
    queryKey: ["users"],
    queryFn: async () => (await api.get("/users")).data,
  });
  const [form, setForm] = useState({ username: "", name: "", role: "executive", password: "" });
  const [busy, setBusy] = useState(false);

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/users", form);
      toast.success("User created");
      setForm({ username: "", name: "", role: "executive", password: "" });
      qc.invalidateQueries({ queryKey: ["users"] });
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  const toggle = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { active: !u.active });
      toast.success(u.active ? "Disabled" : "Enabled");
      qc.invalidateQueries({ queryKey: ["users"] });
    } catch (e) { toast.error(formatError(e)); }
  };

  const resetPw = async (u) => {
    const pw = window.prompt(`New password for ${u.username}:`);
    if (!pw) return;
    try {
      await api.patch(`/users/${u.id}`, { password: pw });
      toast.success("Password reset");
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-users">
      <div>
        <div className="data-label">Admin</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold">Users</h1>
      </div>
      <div className="bg-white border border-border rounded-sm p-4 grid md:grid-cols-5 gap-2 items-end">
        <input className="field-sm md:col-span-1" placeholder="Username"
               value={form.username} onChange={(e) => setForm(f => ({ ...f, username: e.target.value }))}
               data-testid="user-username" />
        <input className="field-sm md:col-span-1" placeholder="Full name"
               value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
               data-testid="user-name" />
        <select className="field-sm md:col-span-1" value={form.role}
                onChange={(e) => setForm(f => ({ ...f, role: e.target.value }))}
                data-testid="user-role">
          <option value="admin">admin</option>
          <option value="manager">manager</option>
          <option value="executive">executive</option>
          <option value="receiver">receiver</option>
        </select>
        <input className="field-sm md:col-span-1" placeholder="Password" type="password"
               value={form.password} onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
               data-testid="user-password" />
        <button onClick={create} className="btn-primary md:col-span-1" disabled={busy || !form.username || !form.password}
                data-testid="user-create">
          <Plus className="w-4 h-4 mr-1" /> Create
        </button>
      </div>
      <div className="bg-white border border-border rounded-sm divide-y divide-border" data-testid="users-list">
        {data.map(u => (
          <div key={u.id} className="p-3 flex items-center gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="font-medium">{u.name} <span className="font-mono text-xs text-muted-foreground">· {u.username}</span></div>
              <div className="badge-flat bg-primary/10 text-primary mt-1">{u.role}</div>
              {!u.active && <span className="badge-flat bg-muted text-muted-foreground ml-2">disabled</span>}
            </div>
            <button onClick={() => resetPw(u)} className="btn-ghost text-xs" data-testid={`user-reset-${u.username}`}>Reset PW</button>
            <button onClick={() => toggle(u)} className="btn-ghost text-xs" data-testid={`user-toggle-${u.username}`}>{u.active ? "Disable" : "Enable"}</button>
          </div>
        ))}
      </div>
    </div>
  );
}
